import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'
import { toast } from 'sonner'
import {
  ArrowRight,
  Barbell,
  GameController,
  GraduationCap,
  Laptop,
  Leaf,
  PaintBrush,
  Rocket,
  Sparkle,
  VideoCamera,
} from '@phosphor-icons/react'
import DraftImportModal from '../components/DraftImportModal'
import { track, EVT } from '../services/analytics'

// ─── Data ────────────────────────────────────────────────────────────────────

// La única pregunta del onboarding. Icono de trazo en vez de emoji: es la
// misma familia que usa el resto del producto.
const CREATOR_TYPES = [
  { id: 'emprendedor', label: 'Emprendedor', icon: Rocket },
  { id: 'educador', label: 'Educador', icon: GraduationCap },
  { id: 'lifestyle', label: 'Lifestyle', icon: Leaf },
  { id: 'gaming', label: 'Gaming', icon: GameController },
  { id: 'tecnologia', label: 'Tecnología', icon: Laptop },
  { id: 'arte', label: 'Arte', icon: PaintBrush },
  { id: 'fitness', label: 'Fitness', icon: Barbell },
  { id: 'otro', label: 'Otro', icon: Sparkle },
]

// El tema del guion de regalo ya no se pregunta: se propone a partir del
// nicho. Se elige al azar para que dos creadores del mismo tipo no entren con
// el mismo guion, y se cambia en el editor como cualquier otro.
const IDEA_SEEDS = {
  emprendedor: [
    'La verdad incómoda sobre construir lo que estoy construyendo',
    'Qué me habría ahorrado saber cuando empecé',
    'El error que todos cometen en mi nicho',
  ],
  educador: [
    'El concepto que explico mal todos los días',
    'Una idea que enseñar requiere desaprender primero',
    'Lo que nadie te cuenta sobre aprender rápido',
  ],
  lifestyle: [
    'La rutina que me parecía genial y en realidad me estaba agotando',
    'Un cambio pequeño que ha transformado mi semana',
    'Por qué dejé de intentar ser perfecto',
  ],
  gaming: [
    'La mecánica más infravalorada del juego',
    'Lo que aprendí perdiendo 100 partidas seguidas',
    'Por qué este jefe es un problema de diseño, no de skill',
  ],
  tecnologia: [
    'Por qué esta herramienta no reemplaza lo que pensabas',
    'La feature que nadie usa pero lo cambia todo',
    'Lo que nadie te dice antes de migrar a X',
  ],
  arte: [
    'El proceso que casi abandono y por qué seguí',
    'Una técnica mal nombrada que uso todos los días',
    'Lo que me enseñó mi peor obra',
  ],
  fitness: [
    'La creencia sobre entrenar que me frenó 3 años',
    'Por qué el progreso visible llega cuando dejas de mirarlo',
    'El error que hace todo el mundo en el gym',
  ],
  otro: [
    'Una verdad incómoda sobre lo que hago',
    'Lo que me habría ahorrado saber cuando empecé',
    'El momento que lo cambió todo',
  ],
}

const GIFT_FORMAT = 'yt_long'

function seedIdea(creatorType) {
  const pool = IDEA_SEEDS[creatorType] || IDEA_SEEDS.otro
  return pool[Math.floor(Math.random() * pool.length)]
}

// ─── Main component ───────────────────────────────────────────────────────────

// Una pregunta cerrada y dentro. El resto del perfil se completa desde el
// producto, cuando el usuario ya ha visto qué mejora al responderlo: el nombre
// sale de la cuenta y los demás campos los pide la barra de progreso.
export default function OnboardingPage() {
  const { user, completeOnboarding } = useAuth()
  const navigate = useNavigate()
  const [creatorType, setCreatorType] = useState('')
  const [saving, setSaving] = useState(false)
  const [showDraftImport, setShowDraftImport] = useState(false)

  // Quien dejó el flujo a medias ya eligió nicho: no se le vuelve a preguntar
  // desde cero.
  useEffect(() => {
    api
      .get('/profile')
      .then(({ data }) => {
        if (data?.creator_type) setCreatorType(data.creator_type)
      })
      .catch(() => {})
  }, [])

  // Terminar la entrada es un paso aparte de guardar el perfil: es lo que abre
  // las rutas del producto. Si falla, seguimos: el perfil ya está guardado y
  // una recarga lo resuelve.
  const finishOnboarding = async () => {
    completeOnboarding()
    try {
      await api.post('/onboarding/complete')
    } catch {
      // sin bloquear la salida
    }
  }

  const saveProfile = async () => {
    try {
      // El nombre viene de la cuenta: preguntarlo otra vez aquí no aporta nada
      // que el registro no sepa ya.
      await api.put('/profile', {
        creator_name: user?.name || '',
        creator_type: creatorType,
      })
      return true
    } catch {
      toast.error('Error al guardar. Inténtalo de nuevo.')
      return false
    }
  }

  const handleStart = async () => {
    if (!creatorType) return

    setSaving(true)
    const ok = await saveProfile()
    if (!ok) {
      setSaving(false)
      return
    }

    const idea = seedIdea(creatorType)
    track(EVT.ONBOARDING_IDEA_SELECTED, { source: 'seed' })
    track(EVT.ONBOARDING_GIFT_GENERATION_STARTED, { format: GIFT_FORMAT })
    const startedAt = performance.now()
    try {
      const res = await api.post('/onboarding/generate-first-script', {
        idea,
        format: GIFT_FORMAT,
      })
      track(EVT.ONBOARDING_GIFT_GENERATION_COMPLETED, {
        format: GIFT_FORMAT,
        duration_ms: Math.round(performance.now() - startedAt),
      })
      await finishOnboarding()
      toast.success('Tu primer guion está listo.')
      navigate(`/scripts?gift=${res.data.script_id}`, {
        state: { giftScriptId: res.data.script_id, giftProjectId: res.data.project_id },
      })
    } catch (err) {
      const detail = err.response?.data?.detail
      const msg =
        typeof detail === 'string' ? detail : 'No se pudo generar el guion. Puedes reintentarlo.'
      track(EVT.ONBOARDING_GIFT_GENERATION_FAILED, { error: msg })
      // El regalo es de una sola vez: si ya se usó, la puerta no puede quedarse
      // cerrada por ello.
      if (err.response?.status === 409) {
        await finishOnboarding()
        navigate('/scripts')
        return
      }
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const enterWithoutGift = async () => {
    setSaving(true)
    if (creatorType) await saveProfile()
    await finishOnboarding()
    setSaving(false)
    navigate('/')
  }

  return (
    <div className="min-h-screen bg-ds-bg flex flex-col items-center justify-center px-4 py-12">
      <div
        className="w-11 h-11 bg-ds-red flex items-center justify-center"
        style={{ borderRadius: 2 }}
      >
        <VideoCamera size={22} weight="fill" color="white" />
      </div>

      <p className="type-label text-ds-red mt-7">Una pregunta y estás dentro</p>

      <h1 className="type-h1 text-ds-text text-display-md text-center mt-4 max-w-[720px]">
        ¿Qué tipo de creador eres?
      </h1>

      <p
        className="text-ds-muted text-[15px] leading-relaxed text-center mt-4 max-w-[520px]"
        style={{ fontFamily: 'Barlow, sans-serif' }}
      >
        Con esto ya podemos escribirte un primer guion. El resto del perfil lo completas después,
        viendo cómo mejora.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-9 w-full max-w-[744px]">
        {CREATOR_TYPES.map(type => {
          const Icon = type.icon
          const selected = creatorType === type.id
          return (
            <button
              key={type.id}
              type="button"
              onClick={() => setCreatorType(type.id)}
              disabled={saving}
              data-testid={`onboarding-type-${type.id}`}
              className={`flex items-center gap-2.5 px-4 py-3.5 border transition-colors disabled:opacity-60 ${
                selected
                  ? 'bg-[#1C0F0E] border-ds-red text-ds-text'
                  : 'bg-ds-surface border-ds-border text-ds-muted hover:border-ds-border-mid hover:text-ds-text'
              }`}
              style={{ borderRadius: 2, fontFamily: 'Barlow, sans-serif' }}
            >
              <Icon size={16} color={selected ? '#D42B1E' : '#555555'} />
              <span className="text-sm font-medium">{type.label}</span>
            </button>
          )
        })}
      </div>

      <button
        onClick={handleStart}
        disabled={!creatorType || saving}
        data-testid="onboarding-start"
        className="btn-accent mt-9 text-[15px] px-9 py-4 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {saving ? (
          <>
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Escribiendo tu primer guion...
          </>
        ) : (
          <>
            Escribir mi primer guion
            <ArrowRight size={16} weight="bold" />
          </>
        )}
      </button>

      <p className="text-ds-muted text-xs mt-4" style={{ fontFamily: 'Barlow, sans-serif' }}>
        Gratis, 0 créditos. Podrás cambiarlo todo después.
      </p>

      {/* Quien ya tiene material no quiere que le inventemos una idea */}
      <div
        className="flex items-center gap-3 mt-6 text-xs"
        style={{ fontFamily: 'Barlow, sans-serif' }}
      >
        <button
          type="button"
          onClick={() => setShowDraftImport(true)}
          disabled={saving}
          data-testid="onboarding-draft-import"
          className="text-ds-muted hover:text-ds-text transition-colors disabled:opacity-40"
        >
          Traer un borrador a medias
        </button>
        <span className="text-ds-faint">·</span>
        <button
          type="button"
          onClick={enterWithoutGift}
          disabled={saving}
          data-testid="onboarding-skip"
          className="text-ds-muted hover:text-ds-text transition-colors disabled:opacity-40"
        >
          Entrar sin guion
        </button>
      </div>

      <DraftImportModal
        open={showDraftImport}
        onClose={() => setShowDraftImport(false)}
        onImported={() => {
          // El modal navega al editor acto seguido, así que la puerta tiene que
          // estar abierta ya cuando se evalúe la ruta.
          finishOnboarding()
        }}
      />
    </div>
  )
}
